import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Link2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import type { ComercialUnitRow } from "@/services/comercial";
import { fetchChaoUnitLinks, saveChaoUnitLinks } from "@/services/chaoDeObra";

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  costCenterLabel: string;
  units: ComercialUnitRow[];
  constructiveUnitOptions: { value: string; label: string }[];
  onSaved: (links: Record<string, string>) => void;
}

export function ChaoUnitLinksModal({
  open,
  onClose,
  costCenterId,
  costCenterLabel,
  units,
  constructiveUnitOptions,
  onSaved,
}: Props) {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedUnits = useMemo(
    () => [...units].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [units],
  );

  const load = useCallback(async () => {
    if (!costCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchChaoUnitLinks(costCenterId);
      if (res.success) {
        setDraft(res.links ?? {});
      } else {
        setError(res.error ?? "Não foi possível carregar os vínculos.");
        setDraft({});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar os vínculos.");
      setDraft({});
    } finally {
      setLoading(false);
    }
  }, [costCenterId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await saveChaoUnitLinks(costCenterId, draft);
      if (!res.success) {
        setError(res.error ?? "Falha ao salvar.");
        return;
      }
      onSaved(draft);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const selectOptions = useMemo(
    () => [{ value: "", label: "— Sem vínculo —" }, ...constructiveUnitOptions],
    [constructiveUnitOptions],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Vínculo unidade imóvel × unidade construtiva"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void onSave()} loading={saving} disabled={loading}>
            Salvar vínculos
          </Button>
        </div>
      }
    >
      <p className="text-sm text-slate-600 mb-4">
        Obra: <strong>{costCenterLabel}</strong>. Associe cada unidade de imóvel à unidade construtiva do orçamento
        Sienge para filtrar a listagem. A API não expõe essa relação — os vínculos ficam salvos neste sistema.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {constructiveUnitOptions.length === 0 && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Nenhuma unidade construtiva foi encontrada no orçamento desta obra. Aplique o centro de custo e confira o
          orçamento no Sienge.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Carregando vínculos…</p>
      ) : (
        <div className="max-h-[min(55vh,520px)] overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
          {sortedUnits.map((u) => (
            <div
              key={u.id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 bg-white hover:bg-slate-50/80"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{u.name}</p>
                <p className="text-xs text-slate-500 font-mono">ID {u.id}</p>
              </div>
              <div className="w-full sm:w-[min(280px,45%)] flex-shrink-0">
                <Select
                  label="Unidade construtiva"
                  value={draft[u.id] ?? ""}
                  onChange={(e) =>
                    setDraft((prev) => {
                      const next = { ...prev };
                      const v = e.target.value.trim();
                      if (v) next[u.id] = v;
                      else delete next[u.id];
                      return next;
                    })
                  }
                  options={selectOptions}
                  placeholder="Selecione…"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" />
        {sortedUnits.length} unidade{sortedUnits.length !== 1 ? "s" : ""} neste centro de custo
      </p>
    </Modal>
  );
}
